/**
 * 프로젝트 — 대화를 묶는 사용자 작업 공간(F-6).
 *
 * 인가는 **백엔드 질의**가 쥔다(모든 조회가 소유자 조건을 WHERE 에 넣는다). 여기서 거르지
 * 않는 이유는 `threads.ts` 와 같다 — 프런트 필터는 인가가 아니라 표시일 뿐이다.
 *
 * 목록에는 대화 제목이 없다(개수뿐). 프로젝트를 열 때 그 안의 대화가 한 번 흐른다.
 */
import { request } from '@/api/client'
import { API } from '@/api/paths'
import type { ThreadSummary } from '@/chat/threads'

export interface Project {
  project_id: string
  name: string
  description: string | null
  /** 이 프로젝트의 모든 대화에 적용되는 사용자 지침. 비어 있는 것이 정상이다. */
  instructions: string | null
  thread_count: number
  created_at: string | null
  updated_at: string | null
}

export async function fetchProjects(): Promise<Project[]> {
  return await request<Project[]>(API.projects)
}

export async function fetchProject(projectId: string): Promise<Project> {
  return await request<Project>(API.project(projectId))
}

export async function fetchProjectThreads(projectId: string): Promise<ThreadSummary[]> {
  const body = await request<{ threads: ThreadSummary[] }>(API.projectThreads(projectId))
  return body.threads
}

export async function createProject(name: string): Promise<Project> {
  return await request<Project>(API.projects, { method: 'POST', body: { name } })
}

/**
 * 부분 수정 — **보낸 키만** 바뀐다.
 *
 * 호출부가 `{ description }` 만 넘기면 지침은 그대로다. 반대로 `{ instructions: null }` 은
 * "비우기"라는 의도된 값이다. 이 구분이 백엔드의 `exclude_unset` 과 짝이므로, 여기서
 * 편의상 전체 객체를 보내도록 바꾸면 설명만 고치는 화면이 지침을 조용히 지우게 된다.
 */
export async function updateProject(
  projectId: string,
  changes: Partial<Pick<Project, 'name' | 'description' | 'instructions'>>,
): Promise<Project> {
  return await request<Project>(API.project(projectId), { method: 'PATCH', body: changes })
}

/** 프로젝트만 지운다 — 안의 대화는 남고 소속만 풀린다(백엔드 FK SET NULL). */
export async function deleteProject(projectId: string): Promise<void> {
  await request<{ deleted: boolean }>(API.project(projectId), { method: 'DELETE' })
}

/** 대화를 프로젝트에 넣거나(id) 뺀다(null). 이미 시작된 대화를 옮기는 유일한 경로. */
export async function setThreadProject(
  threadId: string,
  projectId: string | null,
): Promise<void> {
  await request<{ thread_id: string }>(API.threadProject(threadId), {
    method: 'PUT',
    body: { project_id: projectId },
  })
}
